import React from 'react'
import {Mail, Smartphone, Clock, CheckCircle, ArrowRight} from 'lucide-react'

export default function Blog() {
  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-950 to-blue-900">
      {/* Hero Section */}
      <section className="py-20 border-b border-white/10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-block mb-4">
            <span className="bg-red-600/20 text-red-400 px-4 py-2 rounded-full text-sm font-semibold border border-red-500/30">
              📖 Modern Traditions Blog
            </span>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6">
            Why Texting Santa is the New<br />
            <span className="text-red-500">'Letter to the North Pole'</span>
          </h1>
          <p className="text-xl text-white/80 mb-4">
            Discover how technology is transforming holiday traditions for families in 2025
          </p>
          <p className="text-white/60">Published December 2024 | 5 min read</p>
        </div>
      </section>

      {/* Main Article */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <article className="bg-white/10 backdrop-blur-sm rounded-3xl p-8 sm:p-12 border border-white/20">
            {/* Introduction */}
            <div className="mb-12">
              <p className="text-lg text-white/90 leading-relaxed mb-6">
                For generations, children have written heartfelt letters to Santa Claus, carefully addressing them to the North Pole and waiting weeks for a response. But in 2025, a new tradition is taking over: texting Santa. Here's why modern families are choosing SMS over traditional USPS Santa Letters.
              </p>
            </div>

            {/* Comparison Section */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-white mb-8 flex items-center">
                <Mail className="w-8 h-8 text-red-500 mr-3" />
                Traditional Letters vs. Modern Texts
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Traditional Letters */}
                <div className="bg-blue-900/30 rounded-xl p-6 border border-blue-400/30">
                  <h3 className="text-xl font-bold text-white mb-4">USPS Santa Letters</h3>
                  <ul className="space-y-3 text-white/70">
                    <li className="flex items-start">
                      <span className="text-red-400 mr-2">•</span>
                      <span>Wait 2-4 weeks for a response</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-red-400 mr-2">•</span>
                      <span>Requires stamps and envelopes</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-red-400 mr-2">•</span>
                      <span>Limited to one annual exchange</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-red-400 mr-2">•</span>
                      <span>Generic "Dear Santa Letter" templates</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-red-400 mr-2">•</span>
                      <span>Physical letter can get lost in mail</span>
                    </li>
                  </ul>
                </div>

                {/* Modern Texting */}
                <div className="bg-red-600/20 rounded-xl p-6 border-2 border-red-500">
                  <h3 className="text-xl font-bold text-white mb-4">Texting Santa</h3>
                  <ul className="space-y-3 text-white/90">
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-green-400 mr-2 mt-0.5 flex-shrink-0" />
                      <span>Get responses in minutes, not weeks</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-green-400 mr-2 mt-0.5 flex-shrink-0" />
                      <span>No stamps, envelopes, or trips to the post office</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-green-400 mr-2 mt-0.5 flex-shrink-0" />
                      <span>Ongoing conversations throughout the season</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-green-400 mr-2 mt-0.5 flex-shrink-0" />
                      <span>Personalized Message from Santa with child's name</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-green-400 mr-2 mt-0.5 flex-shrink-0" />
                      <span>100% delivery guarantee</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Speed & Convenience */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-white mb-6 flex items-center">
                <Clock className="w-8 h-8 text-red-500 mr-3" />
                The Speed Advantage
              </h2>
              <div className="bg-white/5 rounded-xl p-6 border border-white/10">
                <p className="text-lg text-white/90 leading-relaxed mb-4">
                  Remember when you had to wait anxiously for a Letter from Santa to arrive by mail? Those days are over. With TextingSanta, your child can send their wish list and receive a personalized response from Santa's Little Helper team in mere minutes.
                </p>
                <p className="text-lg text-white/90 leading-relaxed">
                  This instant gratification doesn't diminish the magic—it enhances it. Children stay engaged throughout December, receiving encouragement messages, behavior reminders, and exciting updates about preparations at the North Pole.
                </p>
              </div>
            </div>

            {/* Digital Santa Signature */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-white mb-6">The Digital Santa Signature</h2>
              <p className="text-lg text-white/90 leading-relaxed mb-6">
                While traditional USPS Santa Letters feature a printed Santa Signature, our digital service includes authenticated messages with Santa's official digital seal. Each text message is uniquely coded and verifiable, proving it came directly from the North Pole Communication Hub.
              </p>
              <div className="bg-gradient-to-r from-red-600/20 to-blue-600/20 rounded-xl p-6 border border-red-500/30">
                <p className="text-white/90 text-center italic">
                  "Parents can save these special text conversations forever, creating a digital memory book that's more personal than any paper letter could ever be."
                </p>
              </div>
            </div>

            {/* Real Parent Testimonials */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-white mb-6">What Parents Are Saying</h2>
              <div className="space-y-4">
                <div className="bg-white/5 rounded-xl p-6 border border-white/10">
                  <p className="text-white/90 mb-3">
                    "My kids were amazed when Santa texted them by name! It's so much better than waiting for a generic letter from USPS. They text Santa throughout December now."
                  </p>
                  <p className="text-white/60 text-sm">- Jennifer M., Parent of two, Chicago</p>
                </div>
                <div className="bg-white/5 rounded-xl p-6 border border-white/10">
                  <p className="text-white/90 mb-3">
                    "As a working mom, I love that I don't have to remember to mail a Dear Santa Letter. TextingSanta does it all digitally, and my daughter gets instant responses."
                  </p>
                  <p className="text-white/60 text-sm">- Maria L., Parent, Miami</p>
                </div>
              </div>
            </div>

            {/* Conclusion */}
            <div className="mb-8">
              <h2 className="text-3xl font-bold text-white mb-6">The Future of Holiday Traditions</h2>
              <p className="text-lg text-white/90 leading-relaxed mb-6">
                While we cherish the nostalgia of traditional USPS Santa Letters, the reality is that today's children live in a digital world. They're more excited by a personalized text Message from Santa than a paper letter that arrives weeks late.
              </p>
              <p className="text-lg text-white/90 leading-relaxed mb-6">
                TextingSanta doesn't replace the magic of Christmas—it amplifies it for a new generation. We're not abandoning tradition; we're evolving it for 2025 and beyond.
              </p>
            </div>

            {/* CTA */}
            <div className="bg-gradient-to-r from-red-600 to-blue-600 rounded-2xl p-8 text-center">
              <h3 className="text-2xl font-bold text-white mb-4">Ready to Start Your New Tradition?</h3>
              <p className="text-white/90 mb-6">
                Join 100,000+ families who've already made the switch from USPS Santa Letters to instant Santa texts
              </p>
              <button className="bg-white text-red-600 hover:bg-gray-100 px-8 py-3 rounded-full font-bold transition-all transform hover:scale-105 inline-flex items-center">
                Start Texting Santa Today
                <ArrowRight className="w-5 h-5 ml-2" />
              </button>
            </div>
          </article>

          {/* Related Articles */}
          <div className="mt-16">
            <h3 className="text-2xl font-bold text-white mb-6">Related Articles</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20 hover:border-red-500 transition-all cursor-pointer">
                <h4 className="text-xl font-bold text-white mb-2">Santa Tracker 2025: Complete Guide</h4>
                <p className="text-white/70 mb-4">Everything you need to know about tracking Santa this Christmas</p>
                <span className="text-red-400 font-semibold inline-flex items-center">
                  Read More <ArrowRight className="w-4 h-4 ml-1" />
                </span>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20 hover:border-red-500 transition-all cursor-pointer">
                <h4 className="text-xl font-bold text-white mb-2">How to Get a Personalized Santa Call</h4>
                <p className="text-white/70 mb-4">Step-by-step guide to scheduling a video call with Santa</p>
                <span className="text-red-400 font-semibold inline-flex items-center">
                  Read More <ArrowRight className="w-4 h-4 ml-1" />
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}
